import { useState, useEffect } from "react"
import { Plus, Wrench, Loader2, ChevronRight, CheckCircle2, Clock, AlertTriangle, AlertCircle, CalendarDays, Building2, Tag } from "lucide-react"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { z } from "zod"
import { toast } from "sonner"

import {
  useMaintenanceIssues,
  useCreateIssue,
} from "@/features/maintenance/hooks/useMaintenance"
import { useProperties } from "@/features/properties/hooks/useProperties"
import { APP_NAME } from "@/lib/appConfig"
import { PageHeader } from "@/components/shared/PageHeader"
import { EmptyState } from "@/components/shared/EmptyState"
import { ListSkeleton } from "@/components/shared/LoadingSkeleton"
import { PullToRefresh } from "@/components/shared/PullToRefresh"
import { StatusBadge } from "@/components/shared/StatusBadge"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { useNavigate } from "react-router-dom"
import type { MaintenanceIssue } from "@/features/maintenance/services/maintenanceService"

const FILTERS = ["all", "open", "in_progress", "completed"] as const
type IssueFilter = (typeof FILTERS)[number]

const issueSchema = z.object({
  property_id: z.string().uuid("Property is required"),
  title: z.string().min(1, "Title is required"),
  description: z.string().optional(),
  priority: z.string().min(1, "Priority is required"),
  category: z.string().optional(),
  scheduled_date: z.string().optional(),
})

type IssueFormValues = z.infer<typeof issueSchema>

function formatDate(dateStr?: string | null) {
  if (!dateStr) return ""
  return new Date(dateStr).toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
  })
}

function IssueCard({ issue }: { issue: MaintenanceIssue }) {
  const navigate = useNavigate()

  const priorityIcons: Record<string, React.ReactNode> = {
    low: <CheckCircle2 className="h-4 w-4 text-blue-500" />,
    medium: <Clock className="h-4 w-4 text-amber-500" />,
    high: <AlertTriangle className="h-4 w-4 text-orange-500" />,
    urgent: <AlertCircle className="h-4 w-4 text-destructive" />,
  }
  const icon = priorityIcons[issue.priority] ?? priorityIcons.medium

  let cardStyle = { border: "border-amber-200 dark:border-amber-500/30", bg: "bg-amber-50/40 dark:bg-amber-500/5" } // open / in_progress (yellowish)
  if (issue.status === "completed") {
    cardStyle = { border: "border-emerald-200 dark:border-emerald-500/30", bg: "bg-emerald-50/40 dark:bg-emerald-500/5" } // completed (greenish)
  } else if (issue.status === "cancelled") {
    cardStyle = { border: "border-border/50", bg: "bg-card/60" } // cancelled (basic white/gray)
  }

  return (
    <Card
      className={`relative rounded-3xl border ${cardStyle.border} ${cardStyle.bg} backdrop-blur-xl shadow-sm hover:shadow-md hover:-translate-y-1 transition-all cursor-pointer overflow-hidden group flex flex-col`}
      onClick={() => navigate(`/maintenance/${issue.id}`)}
    >
      <CardContent className="p-5 flex flex-col h-full flex-1 gap-4">
        {/* Header: Priority & Status */}
        <div className="flex items-center justify-between">
           <div className="flex items-center gap-2">
             {icon}
             <span className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">{issue.priority}</span>
           </div>
           <StatusBadge status={issue.status} />
        </div>

        {/* Title and Description preview */}
        <div className="space-y-1.5 flex-1 mt-1">
          <h3 className="flex items-start justify-between gap-2 text-[17px] font-bold leading-snug group-hover:text-primary transition-colors">
            <span className="line-clamp-2">{issue.title}</span>
            <ChevronRight className="h-5 w-5 opacity-0 group-hover:opacity-100 transition-all text-primary shrink-0" />
          </h3>
          {issue.description && (
            <p className="text-[13px] text-muted-foreground line-clamp-2 leading-relaxed font-medium">
              {issue.description}
            </p>
          )}
        </div>

        {/* Action / Context Meta */}
        <div className="pt-4 border-t border-border/50 grid grid-cols-2 gap-y-2.5 gap-x-4 text-xs font-semibold text-muted-foreground">
          <div className="flex items-center gap-1.5 truncate">
            <Building2 className="h-4 w-4 shrink-0 opacity-70" />
            <span className="truncate">{issue.property?.name ?? "No property"}</span>
          </div>

          <div className="flex items-center gap-1.5 truncate">
            <CalendarDays className="h-4 w-4 shrink-0 opacity-70" />
            <span className="truncate">{issue.scheduled_date ? formatDate(issue.scheduled_date) : "Unscheduled"}</span>
          </div>

          {issue.category && (
            <div className="flex items-center gap-1.5 col-span-2 truncate">
              <Tag className="h-4 w-4 shrink-0 opacity-70" />
              <span className="truncate capitalize">{issue.category.replace(/_/g, " ")}</span>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  )
}

function IssueForm({
  open,
  onOpenChange,
}: {
  open: boolean
  onOpenChange: (v: boolean) => void
}) {
  const createIssue = useCreateIssue()
  const { data: properties } = useProperties()

  const form = useForm<IssueFormValues>({
    resolver: zodResolver(issueSchema),
    defaultValues: {
      property_id: "",
      title: "",
      description: "",
      priority: "medium",
      category: "",
      scheduled_date: "",
    },
  })

  function onSubmit(values: IssueFormValues) {
    const payload = Object.fromEntries(
      Object.entries(values).filter(([, v]) => v !== "" && v !== undefined)
    )
    createIssue.mutate(
      { ...payload, status: "open" },
      {
        onSuccess: () => {
          toast.success("Issue created")
          form.reset()
          onOpenChange(false)
        },
        onError: (err: unknown) => {
          toast.error(err instanceof Error ? err.message : "Failed to create issue")
        },
      }
    )
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="h-[85dvh] overflow-y-auto rounded-t-2xl">
        <SheetHeader className="mb-4">
          <SheetTitle>New Maintenance Issue</SheetTitle>
        </SheetHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4 pb-8">
            <FormField
              control={form.control}
              name="property_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Property *</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select property" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {properties?.map((p) => (
                        <SelectItem key={p.id} value={p.id}>
                          {p.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="title"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Title *</FormLabel>
                  <FormControl>
                    <Input placeholder="e.g. Leaking tap in bathroom" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Describe the issue..."
                      rows={3}
                      className="resize-none"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <div className="grid grid-cols-2 gap-3">
              <FormField
                control={form.control}
                name="priority"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Priority</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Priority" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="low">Low</SelectItem>
                        <SelectItem value="medium">Medium</SelectItem>
                        <SelectItem value="high">High</SelectItem>
                        <SelectItem value="urgent">Urgent</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="category"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Category</FormLabel>
                    <FormControl>
                      <Input placeholder="e.g. Plumbing" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <FormField
              control={form.control}
              name="scheduled_date"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Scheduled Date</FormLabel>
                  <FormControl>
                    <Input type="date" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <Button
              type="submit"
              className="w-full h-12 rounded-xl"
              disabled={createIssue.isPending}
            >
              {createIssue.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                "Create Issue"
              )}
            </Button>
          </form>
        </Form>
      </SheetContent>
    </Sheet>
  )
}

export default function MaintenancePage() {
  const [filter, setFilter] = useState<IssueFilter>("all")
  const [sheetOpen, setSheetOpen] = useState(false)
  const { data: issues, isLoading, isError, refetch } = useMaintenanceIssues()

  useEffect(() => {
    const prev = document.title
    document.title = `Maintenance | ${APP_NAME}`
    return () => { document.title = prev }
  }, [])

  const filtered =
    filter === "all" ? issues : issues?.filter((i) => i.status === filter)

  return (
    <div className="flex flex-col h-full">
      <PageHeader
        title="Maintenance"
        action={
          <Button
            size="sm"
            className="h-9 rounded-xl gap-1"
            onClick={() => setSheetOpen(true)}
          >
            <Plus className="h-4 w-4" />
            Add
          </Button>
        }
      />

      <PullToRefresh onRefresh={async () => { await refetch() }} className="flex-1 pb-nav">
        <div className="px-4 pt-4 shrink-0">
          <div className="rounded-2xl bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/20 p-4 flex flex-col gap-1.5">
            <div className="flex items-center gap-2 text-amber-800 dark:text-amber-400 font-semibold text-sm">
              <Wrench className="h-4 w-4" />
              How Maintenance Works
            </div>
            <p className="text-xs text-amber-700 dark:text-amber-300/80 leading-relaxed">
              Log property issues, assign priorities (<span className="font-medium">Low to Urgent</span>), and track their lifecycle from <span className="font-medium">Open</span> to <span className="font-medium">Completed</span>. 
              Schedule exact dates for repairs to keep your properties in top condition and tenants satisfied.
            </p>
          </div>
        </div>

        <div className="flex gap-2 overflow-x-auto px-4 pt-4 pb-4 scrollbar-none snap-x shrink-0">
          {FILTERS.map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`shrink-0 snap-center h-9 px-5 rounded-full text-sm font-bold transition-all shadow-sm flex items-center justify-center ${filter === f
                  ? "bg-primary text-primary-foreground shadow-glow scale-105 relative z-10"
                  : "bg-card/50 backdrop-blur-md border border-border/50 text-muted-foreground hover:bg-muted/80 hover:text-foreground"
                }`}
            >
              {f === "in_progress"
                ? "In Progress"
                : f.charAt(0).toUpperCase() + f.slice(1)}
            </button>
          ))}
        </div>

        {isLoading && <ListSkeleton count={3} />}

        {isError && (
          <div className="flex flex-col items-center py-16 px-8 text-center">
            <p className="text-sm text-muted-foreground mb-4">
              Failed to load maintenance issues.
            </p>
            <Button variant="outline" size="sm" onClick={() => refetch()}>
              Try again
            </Button>
          </div>
        )}

        {!isLoading && !isError && issues?.length === 0 && (
          <EmptyState
            icon={<Wrench className="h-10 w-10 text-primary" />}
            title="No open issues — great news!"
            description="All clear. Add a maintenance issue when something needs attention."
            action={{ label: "Add Issue", onClick: () => setSheetOpen(true) }}
          />
        )}

        {!isLoading && !isError && filtered && filtered.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 px-4 pt-2 pb-6">
            {filtered.map((issue) => (
              <IssueCard key={issue.id} issue={issue} />
            ))}
          </div>
        )}

        {!isLoading && !isError && filtered?.length === 0 && issues && issues.length > 0 && (
          <div className="flex flex-col items-center py-16 px-8 text-center">
            <p className="text-sm text-muted-foreground">
              No {filter} issues found.
            </p>
          </div>
        )}
      </PullToRefresh>

      <IssueForm open={sheetOpen} onOpenChange={setSheetOpen} />
    </div>
  )
}
